import { diaNegocio, diferenciaDias } from '../fechas';
import type { DetallesChoque } from '../schemas/agenda';

export interface Intervalo {
  inicio: Date;
  fin: Date;
}

export interface HorarioDia {
  entrada: Date;
  salida: Date;
  comidaInicio?: Date | null;
  comidaFin?: Date | null;
  esDescanso: boolean;
}

const MINUTO = 60_000;

/** Intervalos semiabiertos [inicio, fin): los contiguos no se empalman. */
export function seEmpalma(a: Intervalo, b: Intervalo): boolean {
  return a.inicio < b.fin && b.inicio < a.fin;
}

export type ResultadoRango = { ok: true } | { ok: false; motivo: 'PASADO' | 'FUERA_DE_HORIZONTE' };

/**
 * Una cita puede agendarse desde hoy (incluidas horas ya pasadas, para registrar a quien llegó
 * sin cita) hasta `horizonteDias` días de negocio después (inclusive).
 * Ej.: pedida el 23-mar con horizonte 60 → hasta el 22-may.
 */
export function validarRangoAgenda(inicio: Date, ahora: Date, horizonteDias: number): ResultadoRango {
  const dias = diferenciaDias(diaNegocio(ahora), diaNegocio(inicio));
  if (dias < 0) return { ok: false, motivo: 'PASADO' };
  if (dias > horizonteDias) return { ok: false, motivo: 'FUERA_DE_HORIZONTE' };
  return { ok: true };
}

/** `libre`: se puede agendar. `ocupado`: ese minuto ya tiene cita (o es la comida). `no_cabe`: está libre, pero el servicio no termina antes de la siguiente cita o la comida. */
export type EstadoHorario = 'libre' | 'ocupado' | 'no_cabe';

export interface HorarioRejilla extends Intervalo {
  estado: EstadoHorario;
}

/**
 * Todos los horarios del día para un servicio de `duracionMin`, alineados a una rejilla de
 * `intervaloMin` que arranca en la hora de entrada, cada uno con su estado.
 * La salida limita el inicio, no el fin: la última cita puede iniciar a la hora de salida.
 */
export function calcularRejilla(params: {
  horario: HorarioDia;
  ocupados: Intervalo[];
  duracionMin: number;
  intervaloMin: number;
  desde?: Date;
}): HorarioRejilla[] {
  const { horario, ocupados, duracionMin, intervaloMin, desde } = params;
  if (horario.esDescanso || duracionMin <= 0 || intervaloMin <= 0) return [];

  const bloqueos: Intervalo[] = [...ocupados];
  if (horario.comidaInicio && horario.comidaFin) bloqueos.push({ inicio: horario.comidaInicio, fin: horario.comidaFin });

  const paso = intervaloMin * MINUTO;
  const duracion = duracionMin * MINUTO;
  let cursor = horario.entrada.getTime();
  if (desde && desde.getTime() > cursor) {
    cursor += Math.ceil((desde.getTime() - cursor) / paso) * paso;
  }

  const rejilla: HorarioRejilla[] = [];
  for (; cursor <= horario.salida.getTime(); cursor += paso) {
    const candidato = { inicio: new Date(cursor), fin: new Date(cursor + duracion) };
    const casilla = { inicio: candidato.inicio, fin: new Date(cursor + paso) };
    const estado: EstadoHorario = !bloqueos.some((b) => seEmpalma(candidato, b))
      ? 'libre'
      : bloqueos.some((b) => seEmpalma(casilla, b))
        ? 'ocupado'
        : 'no_cabe';
    rejilla.push({ ...candidato, estado });
  }
  return rejilla;
}

/** Horarios libres para un servicio de `duracionMin` (excluye comida y citas ocupadas). */
export function calcularSlots(params: Parameters<typeof calcularRejilla>[0]): Intervalo[] {
  return calcularRejilla(params)
    .filter((h) => h.estado === 'libre')
    .map(({ inicio, fin }) => ({ inicio, fin }));
}

/**
 * Hueco libre más cercano antes y después de `pedido` (mismo día) para un servicio de `duracionMin`.
 * Además de la rejilla, prueba los bordes reales: el fin de cada cita y de la comida, y el último
 * inicio que termina justo cuando empieza una cita o la comida. Misma regla de salida que la
 * rejilla: limita el inicio, no el fin.
 */
export function huecosCercanos(params: {
  horario: HorarioDia;
  ocupados: Intervalo[];
  duracionMin: number;
  intervaloMin: number;
  pedido: Date;
  desde?: Date;
}): { antes: Intervalo | null; despues: Intervalo | null } {
  const { horario, ocupados, duracionMin, intervaloMin, pedido, desde } = params;
  if (horario.esDescanso || duracionMin <= 0 || intervaloMin <= 0) return { antes: null, despues: null };

  const bloqueos: Intervalo[] = [...ocupados];
  if (horario.comidaInicio && horario.comidaFin) bloqueos.push({ inicio: horario.comidaInicio, fin: horario.comidaFin });
  const duracion = duracionMin * MINUTO;
  const entrada = horario.entrada.getTime();
  const salida = horario.salida.getTime();

  const candidatos = new Set<number>();
  for (let c = entrada; c <= salida; c += intervaloMin * MINUTO) candidatos.add(c);
  for (const b of bloqueos) {
    candidatos.add(b.fin.getTime());
    candidatos.add(b.inicio.getTime() - duracion);
  }

  const valido = (c: number) => {
    if (c < entrada || c > salida) return false;
    if (desde && c < desde.getTime()) return false;
    const intervalo = { inicio: new Date(c), fin: new Date(c + duracion) };
    return !bloqueos.some((b) => seEmpalma(intervalo, b));
  };
  const libres = [...candidatos].filter(valido).sort((a, b) => a - b);
  const p = pedido.getTime();
  const aIntervalo = (c: number | undefined) => (c === undefined ? null : { inicio: new Date(c), fin: new Date(c + duracion) });
  return { antes: aIntervalo(libres.filter((c) => c < p).at(-1)), despues: aIntervalo(libres.find((c) => c > p)) };
}

export function primerSlotDisponible(slots: Intervalo[], ahora: Date): Intervalo | null {
  return slots.find((s) => s.inicio.getTime() >= ahora.getTime()) ?? null;
}

/**
 * Agrupa las citas que se cruzan en [inicio, fin), también en cadena (A cruza B y B cruza C → A, B y C).
 * Cada grupo va ordenado por inicio; las contiguas (una termina cuando empieza la otra) no se juntan.
 */
export function agruparEncimadas<T extends { inicio: string; fin: string }>(citas: T[]): T[][] {
  const ordenadas = [...citas].sort((a, b) => new Date(a.inicio).getTime() - new Date(b.inicio).getTime());
  const grupos: T[][] = [];
  let finGrupo = -Infinity;
  for (const c of ordenadas) {
    const inicio = new Date(c.inicio).getTime();
    if (inicio < finGrupo) grupos.at(-1)!.push(c);
    else grupos.push([c]);
    finGrupo = Math.max(inicio < finGrupo ? finGrupo : -Infinity, new Date(c.fin).getTime());
  }
  return grupos;
}

/** Detalles del 409 cuando la hora choca con otra cita del barbero (antes/después); `null` para otros errores. */
export function choqueDe(error: unknown): DetallesChoque | null {
  const e = error as { status?: number; details?: Partial<DetallesChoque> } | null;
  if (!e || e.status !== 409 || !e.details || !('antes' in e.details) || !('despues' in e.details)) return null;
  return { antes: e.details.antes ?? null, despues: e.details.despues ?? null, alternativas: e.details.alternativas ?? [] };
}
